<template>
    <LoadingScreen />
</template>

<script>
import LoadingScreen from '@/components/LoadingScreen';
import PaymentTypes from '@/models/PaymentTypes';
import {
  shouldRenderPaymentTypeView,
  shouldRenderShippingView,
  shouldRenderIdentificationView,
  shouldRenderCheckOtpView
} from '@/helpers/viewRules';
import { logException } from '@/helpers/cieloLogger';
import Vars from '@/config/vars';
import { initClickToPayScript } from '@/helpers/clickToPayHelper';
import { deviceIsAbleToApplePay } from '@/helpers/applePayHelper';
import isHeadlessBrowser from '@/helpers/isHeadless';


export default {
  components: {
    LoadingScreen,
  },

  computed: {
    preorderId() {
      return this.$route.query.id;
    },
  },

  async mounted() {
    this.setInitialLocalizeVeeValidate();
    await this.startCheckout(this.preorderId);
  },

  watch: {
    async preorderId(newValue, oldValue) {
      if (newValue == oldValue) return;

      await this.startCheckout(newValue);
    },
  },

  methods: {
    async startCheckout(preorderId) {
      if (Vars.USE_SIMULATED_MODE) {
        this.$route.query.id = preorderId =
          'a23559c8-1a82-4b6f-ad66-bc4b981618ed';
      }

      if (!preorderId)
        return this.$store.dispatch('pipelineV2/pushTo', 'InvalidLink');

      try {
        await this.$store.dispatch('preorder/fetchPreorder', preorderId);
		    await this.$store.dispatch('dataOnFile/fetchSession');
      } catch (error) {
        logException(error);

        return this.$store.dispatch('pipelineV2/pushTo', 'InvalidLink');
      }

      /**
       * Pipeline que a aplicação vai seguir.
       * Step by step.
       */
      let creditCardPipeline = {
        CardDataCred: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.CREDIT_CARD,
        },
      };

      let debitCardPipeline = {
        CardDataDebit: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.DEBIT_CARD,
        },
      };

      let voucherCardPipeline = {
        CardDataVoucher: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.VOUCHER,
        },
      };

      let qrCodePayPipeline = {
        QrCodePayInstallments: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.QRCODE,
        },
      };

      let clickToPayPipeline = {
        UserValidationC2P: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.CLICK_TO_PAY,
        },
        IdLookUpAuthenticationC2P: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.CLICK_TO_PAY,
        },
        CardListC2P: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.CLICK_TO_PAY,
        },
        UnifiedCardPaymentC2P: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.CLICK_TO_PAY,
        },
      };

      let MultibenefitsCardPipeline = {
        CardDataMultibenefits: {
          when: () =>
            this.$store.state.payment.PaymentType == PaymentTypes.MULTIBENEFITS,
        },
      };

      let googlePayPipeline = {
          GooglePayCardInstallments: {
              when: () =>
                  this.$store.state.payment.PaymentType == PaymentTypes.GOOGLE_PAY,
          },
          GooglePayBillingAddress: {
              when: () =>
                  this.$store.state.payment.PaymentType == PaymentTypes.GOOGLE_PAY,
          },
      };

      let applePayPipeline = {
          ApplePayCardInstallments: {
              when: () =>
                  this.$store.state.payment.PaymentType == PaymentTypes.APPLE_PAY,
          },
      };

      let pixOpenFinancePipeline = {
          PixOpenFinanceBanksList: {
              when: () =>
                  this.$store.state.payment.PaymentType == PaymentTypes.PIX_OPEN_FINANCE,
          },
      };

      let dataOnFilePipeline = {
        DataOnFileReview: {
          when: () =>
              this.$store.state.payment.PaymentType == PaymentTypes.DOF,
        },
        DataOnFileEditCustomer: {
          when: () =>
              this.$store.state.payment.PaymentType == PaymentTypes.DOF,
        },
        DataOnFileAddresses: {
          when: () =>
              this.$store.state.payment.PaymentType == PaymentTypes.DOF,
        },
        DataOnFileEditAddress: {
          when: () =>
              this.$store.state.payment.PaymentType == PaymentTypes.DOF,
        },
        DataOnFileEditCard: {
          when: () =>
              this.$store.state.payment.PaymentType == PaymentTypes.DOF,
        },
      };


      let basePipeline = {
        Identification: { when: () => shouldRenderIdentificationView() },
        CheckOtp: { when: () => shouldRenderCheckOtpView() },
        ShippingAddress: { when: () => shouldRenderShippingView() },
        ChangePaymentType: { when: () => shouldRenderPaymentTypeView() },
        ...creditCardPipeline,
        ...debitCardPipeline,
        ...qrCodePayPipeline,
        ...voucherCardPipeline,
        ...clickToPayPipeline,
        ...MultibenefitsCardPipeline,
        ...googlePayPipeline,
        ...applePayPipeline,
        ...pixOpenFinancePipeline,
        ...dataOnFilePipeline,
        PersonalData: {},
      };

      if (this.$store.state.clickToPayPayment.isClickToPayEnabled && !isHeadlessBrowser()) {
        try {
          await initClickToPayScript();
        } catch (error) {
          logException(error);
        }
      }

      if (this.$store.state.applePayPayment.isApplePayEnabled) {
          try {
              deviceIsAbleToApplePay();
          } catch (error) {
              logException(error);
          }
      }

      /**
       * HEADS UP! Do not remove!
       * The credit card is the default payment type,
       * so the recurring payment flow depends on this
       * declaration, the recurring flow skips the change
       * payment type view, so the default payment type
       * should be defined here. If removed it will certainly
       * break the recurring payment flow.
       *
       * @see viewRules.shouldRenderPaymentTypeView
       */
      this.$store.commit('payment/setPaymentType', PaymentTypes.CREDIT_CARD);

      this.$store.commit('pipelineV2/setPipeline', basePipeline);
      this.$store.dispatch('pipelineV2/pushToFirstStep');
    },

    setInitialLocalizeVeeValidate() {
      let localeName = 'pt_BR';

      import(`vee-validate/dist/locale/${localeName}`).then((locale) => {
        this.$validator.localize(localeName, locale);
        this.currentLocale = localeName;
      });
    },
  },
};
</script>
